import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Search } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SeasonBadge } from "@/components/tyres/common";
import { api, errMsg } from "@/lib/api";

const Num = ({ v, onChange, testId }) => <Input type="number" min="0" value={v} onChange={(e) => onChange(e.target.value)} className="h-8 w-20 text-right" data-testid={testId} />;

export const BaseStockDialog = ({ sid, isAdmin, types, stock, onClose, onSaved }) => {
  const byId = Object.fromEntries(stock.map((s) => [s.type_id, s]));
  const init = Object.fromEntries(types.map((t) => [t.id, { new: String(byId[t.id]?.NUOVA ?? 0), used: String(byId[t.id]?.USATA ?? 0), min: String(byId[t.id]?.min ?? 0) }]));
  const [f, setF] = useState(init);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const list = useMemo(() => types.filter((t) => !q.trim() || t.label.toLowerCase().includes(q.trim().toLowerCase())), [types, q]);
  const set = (id, k) => (v) => setF((p) => ({ ...p, [id]: { ...p[id], [k]: v } }));
  const save = async () => {
    const rows = types.filter((t) => ["new", "used", "min"].some((k) => f[t.id][k] !== init[t.id][k])).map((t) => {
      const n = (k) => (f[t.id][k] === init[t.id][k] || f[t.id][k] === "" ? null : Math.max(0, parseInt(f[t.id][k], 10) || 0));
      return { type_id: t.id, new: n("new"), used: n("used"), min: n("min") };
    });
    if (!rows.length) return onClose();
    setBusy(true);
    try { await api.post("/tyres/stock/base", { station_id: isAdmin ? sid : undefined, rows }); toast.success("Magazzino di base salvato"); onSaved(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-3xl" data-testid="tyre-base-dialog">
        <DialogHeader>
          <DialogTitle>Magazzino di base</DialogTitle>
          <DialogDescription>Imposta la giacenza di partenza (nuove e usate) e la scorta minima di ogni gomma: sotto la scorta minima il magazzino ti avvisa.</DialogDescription>
        </DialogHeader>
        <div className="relative"><Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca gomma..." className="pl-9" data-testid="tyre-base-search" /></div>
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead className="border-b bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500 [&_th]:whitespace-nowrap [&_th]:px-3 [&_th]:py-2">
              <tr><th>Gomma</th><th className="text-right">Nuove</th><th className="text-right">Usate</th><th className="text-right">Scorta minima</th></tr>
            </thead>
            <tbody className="divide-y">
              {list.map((t) => (
                <tr key={t.id} data-testid={`tyre-base-row-${t.id}`}>
                  <td className="px-3 py-2"><p className="text-xs font-medium">{t.label.split(" · ")[0]}</p><SeasonBadge season={t.season} /></td>
                  <td className="px-3 py-2"><div className="flex justify-end"><Num v={f[t.id].new} onChange={set(t.id, "new")} testId={`tyre-base-new-${t.id}`} /></div></td>
                  <td className="px-3 py-2"><div className="flex justify-end"><Num v={f[t.id].used} onChange={set(t.id, "used")} testId={`tyre-base-used-${t.id}`} /></div></td>
                  <td className="px-3 py-2"><div className="flex justify-end"><Num v={f[t.id].min} onChange={set(t.id, "min")} testId={`tyre-base-min-${t.id}`} /></div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Annulla</Button><Button onClick={save} disabled={busy} data-testid="tyre-base-save">{busy ? "Salvataggio..." : "Salva magazzino"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
